import React, { useState, useEffect, startTransition, useCallback } from 'react';
import {
    Button,
    Box,
    CircularProgress,
    Typography,
    FormControl,
    MenuItem,
    Chip,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Avatar,
    alpha,
    TextField,
} from '@mui/material';
import { 
    Delete, 
    Add, 
    ArrowBack, 
    Person as PersonIcon,
    Badge as BadgeIcon,
    CalendarToday as DateIcon,
    Business as BusinessIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'src/view/components/SnackbarService';
import clientService from 'src/clientService';
import { UserResponse, UserRole, UserStatus, Assignment } from 'src/api/Api';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AssignProjectDialog from './AssignProjectDialog';
import { useAppState } from 'src/state-management/state-provider';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import DeveloperPermissionManager from './DeveloperPermissionManager';
import { formatDate } from 'src/util/date-utils';
import Grid from '@mui/material/Grid2';

const UserUpdatePage: React.FC = () => {
    const { dispatch } = useAppState();
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar, snackbarType } = useSnackbar();

    // If navigated from OrgMembersPage, go back there instead of /admin/users
    const fromOrgId = (location.state as { orgId?: string } | null)?.orgId;
    const backPath = fromOrgId ? `/admin/organisation/${fromOrgId}/members` : '/admin/users';
    const backLabel = fromOrgId ? 'Back to Organisation Members' : 'Back to Users';

    const [user, setUser] = useState<UserResponse | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<UserStatus | ''>('');
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [isLoadingUser, setIsLoadingUser] = useState<boolean>(false);
    const [openAssignDialog, setOpenAssignDialog] = useState<boolean>(false);
    const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState<boolean>(false);
    const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);

    // Org membership state (only used when navigated from an org context)
    const [orgMemberId, setOrgMemberId] = useState<string | null>(null);
    const [selectedOrgRole, setSelectedOrgRole] = useState<string>('');
    const [isUpdatingOrgRole, setIsUpdatingOrgRole] = useState<boolean>(false);

    const fetchUser = useCallback(async () => {
        if (userId) {
            setIsLoadingUser(true);
            try {
                const response = await clientService.api.getUserByIdApiV1AdminUsersUserIdGet(userId);
                setUser(response.data);
                setSelectedRoles(response.data.role || []);
                setSelectedStatus(response.data.status as UserStatus || '');
            } catch (error) {
                showSnackbar({ message: 'Failed to fetch user data.', type: snackbarType.ERROR });
            } finally {
                setIsLoadingUser(false);
            }
        }
    }, [userId, showSnackbar, snackbarType.ERROR]);

    const fetchAssignments = useCallback(async () => {
        if (userId) {
            try {
                const response = await clientService.api.getAssignmentsByUserApiV1AssignmentsUserUserIdGet(userId);
                setAssignments(response.data);
            } catch (error) {
                showSnackbar({ message: 'Failed to fetch assignments.', type: snackbarType.ERROR });
            }
        }
        startTransition(() => {
            dispatch({ type: 'ADMIN_SET_PROJECTS', payload: [] });
        });
    }, [userId, showSnackbar, snackbarType.ERROR, dispatch]);

    const fetchOrgMembership = useCallback(async () => {
        if (!fromOrgId || !userId) return;
        try {
            const response = await clientService.api.listOrganisationMembersApiV1OrganisationsOrgIdMembersGet(
                fromOrgId,
                { skip: 0, limit: 500 }
            );
            const match = (response.data.data || []).find(m => m.user_id === userId);
            if (match) {
                setOrgMemberId(match.org_member_id);
                setSelectedOrgRole(match.role);
            }
        } catch {
            // silently ignore — org membership is optional context
        }
    }, [fromOrgId, userId]);

    useEffect(() => {
        fetchUser();
        fetchAssignments();
        fetchOrgMembership();
    }, [fetchUser, fetchAssignments, fetchOrgMembership]);

    const handleOrgRoleChange = async (event: SelectChangeEvent<string>) => {
        if (!orgMemberId) return;
        const newRole = event.target.value;
        setSelectedOrgRole(newRole);
        setIsUpdatingOrgRole(true);
        try {
            await clientService.api.updateOrgMemberRoleApiV1OrgMembersOrgMemberIdRolePatch(
                orgMemberId,
                { role: newRole }
            );
            showSnackbar({ message: 'Organisation role updated successfully.', type: snackbarType.SUCCESS });
        } catch {
            showSnackbar({ message: 'Failed to update organisation role.', type: snackbarType.ERROR });
        } finally {
            setIsUpdatingOrgRole(false);
        }
    };

    const handleRoleChange = (event: SelectChangeEvent<UserRole[]>) => {
        const { target: { value } } = event;
        setSelectedRoles(typeof value === 'string' ? value.split(',') as UserRole[] : value as UserRole[]);
    };

    const handleStatusChange = (event: SelectChangeEvent<UserStatus>) => {
        setSelectedStatus(event.target.value as UserStatus);
    };

    const handleSubmit = async () => {
        if (!userId) return;

        setIsSubmitting(true);
        try {
            await clientService.api.updateUserApiV1AdminUsersUserIdPatch(userId, {
                role: selectedRoles,
                status: selectedStatus as UserStatus,
            });
            showSnackbar({ message: 'User updated successfully!', type: snackbarType.SUCCESS });
            startTransition(() => {
                navigate(backPath);
            });
        } catch (error: any) {
            const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to update user.';
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = (assignmentId: string) => {
        setAssignmentToDelete(assignmentId);
        setOpenConfirmDeleteDialog(true);
    };

    const handleConfirmDeleteAssignment = async () => {
        if (assignmentToDelete) {
            try {
                await clientService.api.deleteAssignmentEndpointApiV1AssignmentsAssignmentIdDelete(assignmentToDelete);
                showSnackbar({ message: 'Assignment deleted successfully!', type: snackbarType.SUCCESS });
                fetchAssignments();
            } catch (error: any) {
                const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to delete assignment.';
                showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
            } finally {
                setOpenConfirmDeleteDialog(false);
                setAssignmentToDelete(null);
            }
        }
    };

    if (isLoadingUser || !user) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: '#10B981' }} />
            </Box>
        );
    }

    const assignedProjectIds = assignments.map(a => a.project.project_id);
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A';

    return (
        <Box sx={{ p: 4, bgcolor: '#0D0D0D', minHeight: '100%', borderRadius: '16px' }}>
            {/* Header / Navigation */}
            <Box sx={{ mb: 4 }}>
                <Button 
                    startIcon={<ArrowBack />} 
                    onClick={() => navigate(backPath)}
                    sx={{ color: '#10B981', textTransform: 'none', mb: 2 }}
                >
                    {backLabel}
                </Button>
                <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, mb: 1 }}>
                    Manage User
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    Update user details, roles, project access, and credits.
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {/* Left Sidebar - User Summary */}
                <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                    <Box sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.03)', 
                        borderRadius: '16px', 
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        p: 3,
                        position: 'sticky',
                        top: 20
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                            <Box sx={{ position: 'relative', mb: 2 }}>
                                <Avatar 
                                    sx={{ 
                                        width: 120, 
                                        height: 120, 
                                        bgcolor: alpha('#10B981', 0.1),
                                        color: '#10B981',
                                        fontSize: '3rem',
                                        fontWeight: 800,
                                        border: '4px solid rgba(16, 185, 129, 0.2)'
                                    }}
                                >
                                    {user.first_name?.[0] || user.email[0].toUpperCase()}
                                </Avatar>
                                <Box sx={{ 
                                    position: 'absolute', 
                                    bottom: 5, 
                                    right: 5, 
                                    width: 16, 
                                    height: 16, 
                                    bgcolor: user.status === UserStatus.Active ? '#10B981' : '#6B7280', 
                                    borderRadius: '50%',
                                    border: '3px solid #1A1A1A'
                                }} />
                            </Box>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.4rem', textAlign: 'center' }}>
                                {fullName}
                            </Typography>
                            <Typography sx={{ color: '#10B981', fontSize: '0.9rem', mb: 2 }}>
                                {user.email}
                            </Typography>
                            <Chip 
                                label={user.status || 'Active'} 
                                size="small"
                                sx={{ 
                                    bgcolor: alpha(user.status === UserStatus.Active ? '#10B981' : '#6B7280', 0.1),
                                    color: user.status === UserStatus.Active ? '#34D399' : '#9CA3AF',
                                    fontWeight: 700,
                                    px: 1
                                }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <BadgeIcon sx={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.3)' }} />
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase' }}>Role</Typography>
                                </Box>
                                <Chip 
                                    label={selectedRoles[0] || 'User'} 
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', color: '#fff', fontWeight: 600 }} 
                                />
                            </Box>

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <BusinessIcon sx={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.3)' }} />
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase' }}>Organisation</Typography>
                                </Box>
                                <Typography sx={{ color: '#fff', fontSize: '1rem' }}>NA</Typography>
                            </Box>

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PersonIcon sx={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.3)' }} />
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase' }}>User ID</Typography>
                                </Box>
                                <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontFamily: 'monospace' }}>{user.user_id}</Typography>
                            </Box>

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <DateIcon sx={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.3)' }} />
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase' }}>Joined On</Typography>
                                </Box>
                                <Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>{formatDate(user.created_at, 'd MMM yyyy, hh:mm a')}</Typography>
                            </Box>
                        </Box>

                        <Button 
                            fullWidth 
                            variant="outlined" 
                            color="error" 
                            sx={{ mt: 4, borderRadius: '10px', textTransform: 'none', borderColor: 'rgba(248, 113, 113, 0.3)' }}
                        >
                            Deactivate User
                        </Button>
                    </Box>
                </Grid>

                {/* Main Content Sections */}
                <Grid size={{ xs: 12, md: 8, lg: 9 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        
                        {/* Section: User Information */}
                        <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.05)', p: 4 }}>
                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>User Information</Typography>
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 4, fontSize: '0.9rem' }}>Update basic profile details and system access.</Typography>
                            
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1 }}>Full Name</Typography>
                                    <TextField 
                                        fullWidth 
                                        value={fullName}
                                        disabled
                                        sx={{ 
                                            '& .MuiOutlinedInput-root': { 
                                                bgcolor: 'rgba(255, 255, 255, 0.02)', 
                                                color: '#fff',
                                                borderRadius: '10px',
                                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1 }}>Email Address</Typography>
                                    <TextField 
                                        fullWidth 
                                        value={user.email}
                                        disabled
                                        sx={{ 
                                            '& .MuiOutlinedInput-root': { 
                                                bgcolor: 'rgba(255, 255, 255, 0.02)', 
                                                color: '#fff',
                                                borderRadius: '10px',
                                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1 }}>Role</Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            multiple
                                            value={selectedRoles}
                                            onChange={handleRoleChange}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => (
                                                        <Chip key={value} label={value} size="small" sx={{ bgcolor: alpha('#fff', 0.1), color: '#fff' }} />
                                                    ))}
                                                </Box>
                                            )}
                                            sx={{ 
                                                bgcolor: 'rgba(255, 255, 255, 0.03)', 
                                                color: '#fff',
                                                borderRadius: '10px',
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#10B981', 0.5) },
                                                '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.5)' }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        bgcolor: '#1A1A1A',
                                                        backgroundImage: 'none',
                                                        color: '#fff',
                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                        mt: 1,
                                                        '& .MuiMenuItem-root': {
                                                            fontSize: '0.9rem',
                                                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                                                            '&.Mui-selected': { bgcolor: alpha('#10B981', 0.1), '&:hover': { bgcolor: alpha('#10B981', 0.15) } }
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            {['Admin', 'User', 'Superadmin'].map((role) => (
                                                <MenuItem key={role} value={role as UserRole}>{role}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1 }}>Status</Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            value={selectedStatus}
                                            onChange={handleStatusChange}
                                            sx={{ 
                                                bgcolor: 'rgba(255, 255, 255, 0.03)', 
                                                color: '#fff',
                                                borderRadius: '10px',
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#10B981', 0.5) },
                                                '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.5)' }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        bgcolor: '#1A1A1A',
                                                        backgroundImage: 'none',
                                                        color: '#fff',
                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                        mt: 1,
                                                        '& .MuiMenuItem-root': {
                                                            fontSize: '0.9rem',
                                                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                                                            '&.Mui-selected': { bgcolor: alpha('#10B981', 0.1), '&:hover': { bgcolor: alpha('#10B981', 0.15) } }
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            {Object.values(UserStatus).map((status) => (
                                                <MenuItem key={status} value={status}>{status}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {/* Org Role — only visible when accessed from an org context */}
                                {fromOrgId && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1 }}>
                                            Organisation Role
                                        </Typography>
                                        <FormControl fullWidth>
                                            <Select
                                                value={selectedOrgRole}
                                                onChange={handleOrgRoleChange}
                                                disabled={isUpdatingOrgRole || !orgMemberId}
                                                displayEmpty
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected ? (
                                                            <Chip 
                                                                label={selected.charAt(0).toUpperCase() + selected.slice(1)} 
                                                                size="small" 
                                                                sx={{ bgcolor: alpha('#fff', 0.1), color: '#fff' }} 
                                                            />
                                                        ) : null}
                                                    </Box>
                                                )}
                                                sx={{ 
                                                    bgcolor: 'rgba(255, 255, 255, 0.03)', 
                                                    color: '#fff',
                                                    borderRadius: '10px',
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#10B981', 0.5) },
                                                    '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.5)' }
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            bgcolor: '#1A1A1A',
                                                            backgroundImage: 'none',
                                                            color: '#fff',
                                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                                            mt: 1,
                                                            '& .MuiMenuItem-root': {
                                                                fontSize: '0.9rem',
                                                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                                                                '&.Mui-selected': { bgcolor: alpha('#10B981', 0.1), '&:hover': { bgcolor: alpha('#10B981', 0.15) } }
                                                            }
                                                        }
                                                    }
                                                }}
                                            >
                                                {['admin', 'manager', 'fieldworker', 'dev'].map((role) => (
                                                    <MenuItem key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {isUpdatingOrgRole && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                <CircularProgress size={12} sx={{ color: '#10B981' }} />
                                                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>Saving…</Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        {/* Section: Credit Management */}
                        <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.05)', p: 4 }}>
                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>Credit Management</Typography>
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 4, fontSize: '0.9rem' }}>Set the total free trial credits and monitor usage for this user.</Typography>
                            
                            <Grid container spacing={3}>
                                {[
                                    { label: 'Total Credits', value: 'NA', sub: 'credits' },
                                    { label: 'Used Credits', value: 'NA', sub: 'credits' },
                                    { label: 'Remaining Credits', value: 'NA', sub: 'credits' },
                                    { label: 'Usage', value: 'NA%', sub: 'of total' }
                                ].map((stat, i) => (
                                    <Grid key={i} size={{ xs: 6, sm: 3 }}>
                                        <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', p: 2, borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1 }}>{stat.label}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                                <Typography sx={{ color: '#fff', fontSize: '1.8rem', fontWeight: 800 }}>{stat.value}</Typography>
                                                <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem' }}>{stat.sub}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                            <Box sx={{ mt: 3, width: '100%', height: '8px', bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px', overflow: 'hidden' }}>
                                <Box sx={{ width: '0%', height: '100%', bgcolor: '#10B981' }} />
                            </Box>
                        </Box>

                        {/* Section: Project Assignments */}
                        <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.05)', p: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                                <Box>
                                    <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>Project Assignments</Typography>
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.9rem' }}>Manage project access and role for this user.</Typography>
                                </Box>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<Add />} 
                                    onClick={() => setOpenAssignDialog(true)}
                                    sx={{ color: '#10B981', borderColor: alpha('#10B981', 0.5), borderRadius: '8px', textTransform: 'none' }}
                                >
                                    Assign Project
                                </Button>
                            </Box>

                            <TableContainer sx={{ border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', bgcolor: 'rgba(255, 255, 255, 0.01)' }}>
                                <Table>
                                    <TableHead sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)' }}>
                                        <TableRow>
                                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>Project</TableCell>
                                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>Role in Project</TableCell>
                                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>Assigned On</TableCell>
                                            <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {assignments.length > 0 ? (
                                            assignments.map((assignment) => (
                                                <TableRow key={assignment.assignment_id} sx={{ '& td': { borderBottom: '1px solid rgba(255, 255, 255, 0.05)' } }}>
                                                    <TableCell>
                                                        <Typography sx={{ color: '#fff', fontWeight: 600 }}>{assignment.project.project_name}</Typography>
                                                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.8rem' }}>{assignment.project.project_developer || 'NA'}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label="Manager" // Mocking role in project as it's not clear in API right now
                                                            size="small" 
                                                            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'rgba(255, 255, 255, 0.7)' }} 
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>{formatDate(assignment.created_at)}</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton onClick={() => handleDeleteClick(assignment.assignment_id)} sx={{ color: 'rgba(255, 255, 255, 0.3)', '&:hover': { color: '#F87171' } }}>
                                                            <Delete sx={{ fontSize: '1.1rem' }} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'rgba(255, 255, 255, 0.3)' }}>
                                                    No projects assigned.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Typography sx={{ mt: 2, color: '#10B981', fontWeight: 600, fontSize: '0.85rem' }}>
                                Total Projects Assigned: {assignments.length}
                            </Typography>
                        </Box>

                        {/* Developer Permissions Section */}
                        {selectedRoles.includes(UserRole.Dev) && userId && (
                            <DeveloperPermissionManager userId={userId} />
                        )}
                        
                        {/* Footer Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, mb: 10 }}>
                            <Button 
                                onClick={() => navigate(backPath)} 
                                sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'none', px: 4, borderRadius: '10px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={isSubmitting || !user}
                                sx={{
                                    bgcolor: '#10B981',
                                    '&:hover': { bgcolor: '#059669' },
                                    color: '#000',
                                    fontWeight: 800,
                                    textTransform: 'none',
                                    px: 4,
                                    py: 1.2,
                                    borderRadius: '10px'
                                }}
                            >
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {openAssignDialog && (
                <AssignProjectDialog
                    open={openAssignDialog}
                    onProjectAssigned={fetchAssignments}
                    userId={userId!}
                    userEmail={user.email}
                    assignedProjectIds={assignedProjectIds}
                    onClose={() => setOpenAssignDialog(false)}
                />
            )}

            <ConfirmationDialog
                open={openConfirmDeleteDialog}
                onCancel={() => setOpenConfirmDeleteDialog(false)}
                onConfirm={handleConfirmDeleteAssignment}
                title="Delete Assignment"
                content="Are you sure you want to delete this project assignment? This action cannot be undone."
            />
        </Box>
    );
};

export default UserUpdatePage;

